import dayjs from "dayjs";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";
import type { CreatePayloadContext } from "@/components/tables/genericCrudTable.createPayload";
import {
  normalizeIntegerIdArray,
  normalizeRelationIdArray,
} from "@/components/tables/genericCrudTable.utils";

const isAnalogFlag = (value: unknown) => value === true || value === "true";

const hasManagerRole = (groupSet: any): boolean =>
  Boolean(groupSet.find((group: any) => group.name === "manager"));

const withPresentGroups = (result: any) =>
  result.group_set === undefined
    ? {}
    : { group_set: normalizeIntegerIdArray(result.group_set) };

const withAllowedRoleFields = (
  result: any,
  ctx: Pick<CreatePayloadContext, "subdomain" | "canEditUserRoles">
) => {
  if (ctx.subdomain === "admin" && ctx.canEditUserRoles) {
    return result;
  }
  const payload = { ...result };
  delete payload.group_set;
  delete payload.direct_group_set;
  return payload;
};

const withDirectGroups = (result: any) =>
  result.direct_group_set === undefined
    ? {}
    : { direct_group_set: normalizeIntegerIdArray(result.direct_group_set) };

const withoutMissingEmailOfAnalogUser = (result: any) => {
  if (result.is_analog === "true" && result.email === undefined) {
    delete result.email;
    return { ...result };
  }
  return result;
};

export const userEntityAdapter: EntityAdapter = {
  entity: "user",
  createEntity: "user/create_worker",
  confirmsFullnameConflict: true,
  adjustCreatePayload: (result, values, ctx) => {
    if (ctx.companySubjectId) {
      return {
        ...result,
        impersonate_subject: ctx.companySubjectId,
        birth_date: dayjs(values.birth_date).format("DD.MM.YYYY"),
        team_id: result.team,
        department_set: normalizeRelationIdArray(result.department_set),
        group_set: normalizeIntegerIdArray(result.group_set),
        ...withDirectGroups(result),
        ignore_fullname_conflict: false,
      };
    }
    if (!ctx.companyId) {
      return {
        ...result,
        impersonate_subject: ctx.selectedChildCompanySubjectId,
        birth_date: dayjs(values.birth_date).format("DD.MM.YYYY"),
        department_set: normalizeRelationIdArray(result.department_set),
        group_set: normalizeIntegerIdArray(result.group_set),
        ...withDirectGroups(result),
        ignore_fullname_conflict: false,
      };
    }
    return result;
  },
  finishCreatePayload: (result, _values, ctx) =>
    withAllowedRoleFields(withoutMissingEmailOfAnalogUser(result), ctx),
  adjustUpdatePayload: (result, values, ctx) => {
    let payload = result;
    if (ctx.companySubjectId) {
      payload = {
        ...payload,
        company_id: ctx.companySubjectId,
        department_set: normalizeRelationIdArray(payload.department_set),
        ...withPresentGroups(payload),
        ...withDirectGroups(payload),
        birth_date: dayjs(values.birth_date).format("DD.MM.YYYY"),
        team_id: payload.team,
      };
    } else if (!ctx.companyId) {
      payload = {
        ...payload,
        impersonate_subject: ctx.selectedChildCompanySubjectId,
        birth_date: dayjs(values.birth_date).format("DD.MM.YYYY"),
        department_set: normalizeRelationIdArray(payload.department_set),
        ...withPresentGroups(payload),
        ...withDirectGroups(payload),
      };
    }
    return withAllowedRoleFields(withoutMissingEmailOfAnalogUser(payload), ctx);
  },
  getSaveBlockReason: (result, presetValues) =>
    presetValues?.is_portal_registered === true &&
    isAnalogFlag(result.is_analog) &&
    !isAnalogFlag(presetValues?.is_analog)
      ? "analog_cannot_set_for_registered_user"
      : undefined,
  defaultSort: { sort: "last_name", tableSort: "last_name", descending: false },
  searchAutoFocus: false,
  bodyCellSx: (user, baseSx) => {
    if (hasManagerRole(user.group_set)) {
      return {
        ...baseSx,
        fontWeight: "bold",
        color: user.is_deleted ? "lightgray" : "inherit",
      };
    }
    return user.is_deleted ? { ...baseSx, color: "lightgray" } : baseSx;
  },
};
